import { useMemo } from 'react'
import { Link } from 'react-router-dom'
import DataGrid from '@components/common/DataGrid'
import KebabMenu from '@components/common/KebabMenu'
import ScheduleStatusCell from './ScheduleStatusCell'
import RunHealthText from './RunHealthText'
import { QUEUE_PATH } from '@config/reportRoutes'
import { fmtWhenInZone } from './queueFormat'
import { lastRunSummary, summarizeRuns } from './scheduleFormat'
import {
  DuplicateIcon, HistoryIcon, PauseIcon, PencilIcon, PlayIcon, SkipIcon, TrashIcon,
} from './queueIcons'

/**
 * The schedules list as a TABLE — Himaad's Section 5 (`Schedules / List` 589:9933).
 *
 * This is a real restructure, not a restyle: what shipped was `ScheduleCard`, one
 * card per schedule with a three-line scan order. The design's columns are
 * Schedule · Status · Cadence · Next run · Last run · Last N runs · Run now ·
 * pencil · kebab.
 *
 * ## Why a table is better here, in the design's own terms
 *
 * The cards put every schedule's facts in a different place vertically, so
 * comparing two schedules — "which of these runs first?", "which one is failing?"
 * — meant reading both cards in full. A standing financial instruction is exactly
 * the kind of thing people compare. Sticky note 3's rule ("one cell, one thing")
 * is what makes the columns readable at 100 rows.
 *
 * ## On the shared DataGrid, deliberately
 *
 * `common/DataGrid`, not a hand-rolled `<table>` — the house rule for list pages,
 * and it matches `QueueTable` next door. Two tables one click apart that disagreed
 * about sorting, paging and the Columns picker is the drift this codebase keeps
 * paying for. It also brings Columns + Export CSV of the schedule list for free.
 *
 * ⚠ Same trap as `QueueTable`: `columns` must NOT depend on a ticking clock, or
 * the memo yields a new array every tick, TanStack rebuilds the cell renderers and
 * every row's kebab remounts — which is how the "⋯" menu appeared to close itself
 * the instant it opened. Nothing here reads a clock.
 *
 * ## Nothing the cards could do was dropped
 *
 * Every capability is carried, because absence in a mock is not a removal and
 * because most of these were fixes someone paid for:
 *
 *   - **Run now** (text button, active rows only) and **Resume** for a paused row.
 *   - **Edit** as a pencil with a real accessible name.
 *   - Kebab: **Skip the next run** (withheld while paused — the backend rejects it
 *     for the same reason), **Pause/Resume**, **Duplicate**, **View run history**,
 *     and **Delete last, behind a divider**.
 *   - **Duplicate stays server-side.** A client-side copy posts a filterless
 *     schedule, because the client mapper omits `params_json` — and a filterless
 *     schedule runs the stored procedure UNSCOPED. This component only calls the
 *     handler; the handler must never be reimplemented locally.
 *   - The **dormant-credential** state, now a first-class Status value rather than
 *     a line of prose (see `ScheduleStatusCell` for why it outranks Active).
 *   - **View-only rows** for a colleague's shared schedule: no controls, and a
 *     "Shared with you" chip so the row does not read as broken.
 *   - Spec 169: a colleague's PRIVATE schedule, shown only to a CB administrator
 *     (`adminAccess`), offers Edit and Pause / Resume — nothing else.
 *
 * ## The "Last N runs" header is measured, not "10"
 *
 * The mock's column reads **"Last 10 runs"** and its cell **"9 of 10 ran"**. Both
 * are unreachable: `retain_count` defaults to 5 and pruning is immediate, so the
 * history never holds ten. The header takes the largest finished-run count
 * actually present, so the column cannot over-claim its own window. See
 * `RunHealthText`.
 */

/*
 * Spec 182 — the run the Last-run cell describes, on the reader's OWN rows only. cb-api
 * already sends `runs: []` for a row the reader does not own (a colleague's result rows
 * carry the owner's data scope — CC-50231, spec 169), and `RunHealthText` already
 * refuses to read them there (`historyWithheld`). This is the same second fence for
 * this cell: a run that somehow reached a colleague's row is never linked or described.
 */
const ownLastRun = (s) => (s.isOwner !== false ? lastRunSummary(s) : null)

const SchedulesTable = ({
  schedules,
  reportNames,
  loading,
  runningId,
  onTogglePaused,
  onEdit,
  onRunNow,
  onSkip,
  onDuplicate,
  onViewHistory,
  onDelete,
}) => {
  /*
   * The widest history any row actually has. Drives the column header so it
   * describes the data rather than an aspiration — and `null` when no row has a
   * finished run, in which case the header says "Recent runs" and promises no
   * number at all.
   */
  const widestHistory = useMemo(() => {
    const counts = (schedules || []).map((s) => summarizeRuns(s.runs || []).total)
    const max = counts.length ? Math.max(...counts) : 0
    return max > 0 ? max : null
  }, [schedules])

  const columns = useMemo(() => [
    {
      accessorKey: 'name',
      header: 'Schedule',
      cell: ({ row }) => {
        const s = row.original
        const name = s.name || (s.reportId ? `Report #${s.reportId}` : 'Scheduled report')
        /*
         * The design's identity line carries FOUR facts: report name, #id, the
         * date-window behaviour, and the visibility scope. All four were on the
         * card's third line as chips; they are one grey line here because sticky
         * note 3 wants one thing per cell and this cell's thing is "which report,
         * whose, and does it still mean this month".
         */
        const reportName = reportNames?.get?.(String(s.reportId))
        const window = s.rollForward && s.rollForward !== 'pinned' ? 'Range rolls forward' : 'Fixed date range'
        const scope = s.scope || (s.shareable ? 'firm' : 'mine')
        /*
         * Spec 169 — a private schedule is visible to its owner AND to Centerbase
         * administrators (Jordan, 2026-10-13), so "Only me" was no longer true. An
         * administrator's view of a colleague's private schedule (`adminAccess`, cb-api's
         * answer) says it is private and whose it is — the CB user name the server sent,
         * or "a colleague" when CB could not say.
         */
        const scopeText = scope === 'firm'
          ? 'Everyone at the firm'
          : scope === 'group'
            ? (s.groupName ? `Group: ${s.groupName}` : 'My group')
            : s.isOwner === false && s.adminAccess === true
              ? `Private to ${s.ownerName || 'a colleague'}`
              : 'Only me and administrators'
        return (
          <div className="min-w-0">
            <div className="truncate text-[13.5px] font-semibold leading-tight text-cb-navy">{name}</div>
            <div className="mt-1 truncate text-[11.5px] text-gray-500">
              {[reportName, s.reportId ? `#${s.reportId}` : null, window, scopeText]
                .filter(Boolean)
                .join(' · ')}
            </div>
          </div>
        )
      },
    },
    {
      id: 'status',
      accessorFn: (s) => (s.needsSignIn ? 'needs sign-in' : s.enabled ? 'active' : 'paused'),
      header: 'Status',
      cell: ({ row }) => (
        <ScheduleStatusCell schedule={row.original} isOwner={row.original.isOwner !== false} />
      ),
    },
    {
      accessorKey: 'description',
      header: 'Cadence',
      cell: ({ row }) => {
        const s = row.original
        /*
         * Greyed while paused — the cadence is still a fact about the schedule, it
         * is just not currently in force.
         *
         * D1 (spec 118) — but greyed to gray-500, NOT the mock's gray-400. This is
         * the actual accessibility defect Himaad raised ("from an accessibility
         * perspective, I don't know if that's readable"), and it is measured.
         *
         * The hexes are the ones tailwindcss 4.2.1 ACTUALLY emits, converted from
         * the oklch in node_modules/tailwindcss/theme.css — not the v3 values these
         * comments first carried, which appear nowhere in this build's output:
         *
         *   text-gray-400 #99a1af at 12.5px normal weight = 2.602:1 on white,
         *                                                   2.490:1 on #f9fafb
         *   text-gray-500 #6a7282                         = 4.836:1 / 4.628:1
         *
         * WCAG 2.1 SC 1.4.3 needs 4.5:1 for normal-size text, so gray-400 fails on
         * both grounds this cell can sit on and gray-500 passes on both. A DEVIATION
         * from the mock, which specifies the lighter grey; recorded in spec 118 §D1.
         * The margin on the hovered ground is only 0.128, which is why the ratio is
         * recomputed from disk in __tests__/contrastClaims.test.js rather than typed.
         *
         * ⚠️ Whether the paused cadence should be demoted AT ALL is still open —
         * Himaad's own words admit the reading "drop the fade, the Paused badge is
         * doing that job". Only the ratio was changed here. See spec 118 §D1.
         *
         * The schedule NAME above is deliberately NOT faded, mock or no mock.
         */
        return (
          <span className={s.enabled ? 'text-[12.5px] text-gray-800' : 'text-[12.5px] text-gray-500'}>
            {s.description || '—'}
          </span>
        )
      },
    },
    {
      accessorKey: 'nextRunAt',
      header: 'Next run',
      cell: ({ row }) => {
        const s = row.original
        /*
         * An em-dash for a paused schedule, per the design — and it is a
         * correctness point, not a style one. `nextRunAt` is still populated on a
         * paused row (the server computes the slot regardless), so printing it
         * would promise a run that the cron will skip. Same for a dormant one.
         */
        /* A testid because this cell carries a promise, and the assertion that it
           does NOT print a paused schedule's `nextRunAt` needs a handle it cannot
           accidentally satisfy from a neighbouring cell — the Cadence cell also
           ends in the same clock time. */
        if (!s.enabled || s.needsSignIn) {
          // gray-500, not gray-400 — spec 118 §D1. At the hexes tailwindcss 4.2.1
          // emits: gray-400 #99a1af is 2.602:1 on white and 2.490:1 on the #f9fafb
          // row-hover ground; gray-500 #6a7282 is 4.836 / 4.628.
          return <span data-testid="schedule-next-run" className="text-gray-500">—</span>
        }
        /*
         * A FOREIGN row cannot be vouched for, and must not be promised.
         *
         * The gate above depends on `needsSignIn`, which the API now withholds on
         * someone else's row (a colleague's credential state is not a shared
         * view's business — `ScheduleStatusCell` says so in writing). That gating
         * fixed a disclosure but broke this cell's invariant in the same move: with
         * `needsSignIn` forced `false`, an enabled-but-DORMANT shared schedule fell
         * through and printed a time the cron will skip — exactly the promise the
         * comment above forbids. Caught by the follow-up review, not by a test.
         *
         * So a foreign row says what is actually known: this is the schedule's
         * PLANNED slot. That is a true statement about the owner's rule (which they
         * chose to share) and stops short of asserting the run will happen, which
         * only the owner's view can know. Neither the disclosure nor the false
         * promise survives.
         */
        if (s.isOwner === false) {
          return (
            <span
              data-testid="schedule-next-run"
              className="text-[12.5px] text-gray-800"
              title="The owner's planned slot. Whether it runs depends on their access, which isn't shown here."
            >
              {s.nextRunAt ? `${fmtWhenInZone(s.nextRunAt, s.timezone)} (planned)` : 'not scheduled'}
            </span>
          )
        }
        return (
          <span data-testid="schedule-next-run" className="text-[12.5px] text-gray-800">
            {s.nextRunAt ? fmtWhenInZone(s.nextRunAt, s.timezone) : 'not scheduled'}
          </span>
        )
      },
    },
    {
      /*
       * CC-53474 (spec 182) — ONE run per cell. "Last run" is the newest run of any
       * kind, and its time, link and facts all come from that one row
       * (`lastRunSummary`). The cell used to take its time from the stored
       * `lastRunAt` (only the scheduler writes it), its link from the newest run and
       * its numbers from the newest READY run — so a Run-now-only schedule said
       * "Hasn't run yet" beside "1 of 1 ran", and a Run now after a scheduled firing
       * printed the scheduler's time over the Run-now run's link and row count.
       *
       * The column id stays `lastRunAt`: DataGrid persists each user's column order
       * and visibility by id (`storageKey="report-schedules"`), and renaming it would
       * silently drop a saved layout. Sorting follows the time the cell prints.
       */
      id: 'lastRunAt',
      accessorFn: (s) => ownLastRun(s)?.at ?? s.lastRunAt ?? null,
      header: 'Last run',
      cell: ({ row }) => {
        const s = row.original
        const last = ownLastRun(s)
        if (last) {
          return (
            <span className="inline-flex flex-col items-start gap-px" data-testid="schedule-last-run">
              {/*
                * A link into the queue for THIS run — a row is a route into the
                * result rather than a dead end. The run list is owner-only and
                * filtered to rows that exist and are unexpired (CC-53438), so the
                * link cannot dangle and never names a colleague's run (spec 169).
                */}
              <Link to={`${QUEUE_PATH}?jobId=${last.jobId}`} className="text-[12.5px] font-medium text-cb-accent-text hover:underline">
                {fmtWhenInZone(last.at, s.timezone)}
              </Link>
              {/* "Run now · 121 rows · 2.2s" — the label says the scheduler did not
                  start this one; gray-500 is 4.836:1 on white, 4.628:1 hovered. */}
              {last.facts && (
                <span data-testid="schedule-last-run-facts" className="tabular-nums text-[11.5px] text-gray-500">{last.facts}</span>
              )}
            </span>
          )
        }
        {/* "by YOU" is a claim about the reader, and on a colleague's shared row it
            is simply false (QA, 2026-09-03) — the `isOwner` flag needed to say so
            was already on the row and only this cell ignored it. */}
        if (!s.enabled && !s.lastRunAt) {
          return (
            <span className="text-[12.5px] text-gray-500">
              {s.isOwner !== false ? 'paused by you' : 'paused'}
            </span>
          )
        }
        /*
         * A colleague's row (shared, or spec 169's administrator view of a private
         * one): the API withholds its runs, so all that is known is the stored
         * `lastRunAt` — the owner's last SCHEDULED firing. Say exactly that. A Run now
         * by the owner is invisible here, so "Hasn't run yet" would be a claim this
         * reader cannot make; and an unlabelled time would read as "the newest run",
         * which the column means on the owner's own rows. Same shape as the Next run
         * cell's "(planned)".
         */
        if (s.isOwner === false) {
          return s.lastRunAt ? (
            <span
              data-testid="schedule-last-run"
              className="text-[12.5px] text-gray-800"
              title="The owner's last scheduled run. Runs they start with Run now aren't shown here."
            >
              {`${fmtWhenInZone(s.lastRunAt, s.timezone)} (scheduled)`}
            </span>
          ) : (
            <span className="text-[12.5px] text-gray-500">No scheduled run yet</span>
          )
        }
        /*
         * The owner's row with no run listed. "Hasn't run yet" only when the
         * scheduler has never fired it either; a stored time with no run behind it
         * (every run dismissed from the tray) is still true, so it is printed as
         * plain text with no link — there is no run left to open.
         */
        if (!s.lastRunAt) return <span className="text-[12.5px] text-gray-500">Hasn&apos;t run yet</span>
        return <span className="text-[12.5px] text-gray-800">{fmtWhenInZone(s.lastRunAt, s.timezone)}</span>
      },
    },
    {
      id: 'health',
      accessorFn: (s) => summarizeRuns(s.runs || []).failureCount,
      header: widestHistory ? `Last ${widestHistory} runs` : 'Recent runs',
      meta: { pickerLabel: 'Run health' },
      cell: ({ row }) => (
        <RunHealthText
          runs={row.original.runs}
          paused={!row.original.enabled}
          // The server withholds a colleague's run rows on purpose, so an empty
          // array here means "not shown", not "none" — see the note on the prop.
          historyWithheld={row.original.isOwner === false}
          onViewHistory={() => onViewHistory(row.original)}
        />
      ),
    },
    {
      id: 'actions',
      header: '',
      meta: { pickerLabel: 'Actions' },
      enableSorting: false,
      cell: ({ row }) => {
        const s = row.original
        const name = s.name || (s.reportId ? `Report #${s.reportId}` : 'Scheduled report')
        // `isOwner` is only ever absent on a payload from an older worker, where
        // every row WAS the caller's — so defaulting to true keeps that correct.
        const isOwner = s.isOwner !== false

        /*
         * Spec 169 — a CB administrator looking at a colleague's PRIVATE schedule may edit
         * it and pause / resume it (Jordan, 2026-10-13), and nothing else: Run now, Skip,
         * Duplicate, Run history and Delete stay the owner's (cb-api refuses them too).
         * `adminAccess` is cb-api's answer for this reader; strictly `=== true`.
         */
        if (!isOwner && s.adminAccess === true) {
          return (
            <div className="flex shrink-0 items-center justify-end gap-1.5">
              {!s.enabled && (
                <button type="button" onClick={() => onTogglePaused(s)} className="btn-cb-toolbar border-rc-seam! whitespace-nowrap shadow-xs">
                  Resume
                </button>
              )}
              <button
                type="button"
                onClick={() => onEdit(s)}
                aria-label={`Edit ${name} as an administrator`}
                className="btn-cb-toolbar border-rc-seam! shadow-xs"
              >
                <PencilIcon />
              </button>
              <KebabMenu
                label={`More actions for ${name}`}
                items={[{
                  key: 'pause',
                  label: s.enabled ? 'Pause schedule' : 'Resume schedule',
                  icon: s.enabled ? <PauseIcon /> : <PlayIcon />,
                  onSelect: () => onTogglePaused(s),
                }]}
              />
            </div>
          )
        }

        if (!isOwner) {
          return (
            <span className="whitespace-nowrap rounded-md bg-gray-100 px-2 py-1 text-[10.5px] font-medium text-gray-600">
              Shared with you
            </span>
          )
        }

        const items = [
          // Withheld while paused: a schedule that is not going to fire has no
          // next run to skip, and the backend rejects the call for that reason.
          ...(s.enabled
            ? [{ key: 'skip', label: 'Skip the next run', icon: <SkipIcon />, onSelect: () => onSkip(s) }]
            : []),
          {
            key: 'pause',
            label: s.enabled ? 'Pause schedule' : 'Resume schedule',
            icon: s.enabled ? <PauseIcon /> : <PlayIcon />,
            onSelect: () => onTogglePaused(s),
          },
          // MUST stay server-side — see the note in this file's header.
          { key: 'duplicate', label: 'Duplicate', icon: <DuplicateIcon />, onSelect: () => onDuplicate(s) },
          { key: 'history', label: 'View run history', icon: <HistoryIcon />, onSelect: () => onViewHistory(s) },
          {
            key: 'delete',
            label: 'Delete schedule',
            icon: <TrashIcon />,
            danger: true,
            dividerBefore: true,
            onSelect: () => onDelete(s),
          },
        ]

        return (
          <div className="flex shrink-0 items-center justify-end gap-1.5">
            {s.enabled ? (
              <button
                type="button"
                onClick={() => onRunNow(s)}
                disabled={runningId === s.id}
                className="btn-cb-toolbar border-rc-seam! whitespace-nowrap shadow-xs"
              >
                {runningId === s.id ? 'Starting…' : 'Run now'}
              </button>
            ) : (
              /*
               * A3 (spec 118) — this was `.btn-cb-primary`, a filled #1d4ed8 CTA, so a
               * paused row shouted louder than an active one. Himaad: "the CTA here for
               * Run now, that color looks good ... The resume, I think we can tone that
               * down too." So Resume now wears exactly what Run now wears — they occupy
               * the same slot in the same column and only differ by state.
               *
               * A CALL-SITE swap ONLY. `.btn-cb-primary` itself must not change: two e2e
               * specs pin it to 38px / rgb(29,78,216) / white / 6px radius
               * (reports-grid-audit.spec.js:509-514 and
               * financial-reports-audit.spec.js:217-222) and it paints every CTA in the app.
               *
               * The mock goes further and has no Resume BUTTON at all — resuming happens by
               * clicking the status badge. Deliberately NOT adopted here; see spec 118 §A3.
               *
               * ⚠️ WHY `border-rc-seam!` — same reasoning as "View report"
               * (JobRowActions.jsx, spec 118 §A2), where it is spelled out in full.
               * Resume's fill WAS --color-rc-chip-text #1d4ed8 (6.702:1 on a white row,
               * 6.413:1 on a hovered one). Outlined, the only non-text thing identifying
               * it is a 1px border, and `.btn-cb-toolbar`'s own --color-rc-control
               * #8b95a5 is 3.026:1 at rest but 2.896:1 once the row tints to #f9fafb via
               * `hover:bg-gray-50` (common/DataGrid.jsx:1571) — UNDER SC 1.4.11's 3.0:1,
               * which src/index.css:701 records as FAIL on that exact ground. So all
               * three buttons in this cell override it to --color-rc-seam #6b7280
               * (4.834 / 4.626, PASS on both), the colour `.btn-cb-toolbar:hover`
               * already sets — no new hex, and the same substitution index.css:439
               * made on `.btn-cb-neutral.btn-cb-split-r` for this reason (CC-53362).
               *
               * The `!` is load-bearing and NOT decoration. A bare `border-rc-seam`
               * really would be inert — `.btn-cb-toolbar` is unlayered and its `border`
               * shorthand beats a layered NORMAL declaration. Tailwind v4's important
               * modifier emits `!important`, which per CSS Cascade 5 is sorted before
               * layer origin and therefore wins. Verified by compiling this repo's real
               * src/index.css with its own tailwindcss 4.2.1; both forms are pinned in
               * __tests__/contrastClaims.test.js. An earlier version of this comment
               * said only src/index.css could fix the colour. That was false.
               */
              <button type="button" onClick={() => onTogglePaused(s)} className="btn-cb-toolbar border-rc-seam! whitespace-nowrap shadow-xs">
                Resume
              </button>
            )}
            {/* `px-2 py-1.5` used to sit on this button and both were INERT:
                .btn-cb-toolbar is in the unlayered src/index.css and defeats px-/py-
                utilities regardless of specificity, so it has always rendered the
                class's own 0.375rem/0.75rem. Deleted rather than left implying an 8px
                inset that was never on screen — zero visual change. */}
            <button
              type="button"
              onClick={() => onEdit(s)}
              aria-label={`Edit ${name}`}
              className="btn-cb-toolbar border-rc-seam! shadow-xs"
            >
              <PencilIcon />
            </button>
            <KebabMenu label={`More actions for ${name}`} items={items} />
          </div>
        )
      },
    },
  ], [reportNames, runningId, widestHistory, onTogglePaused, onEdit, onRunNow, onSkip, onDuplicate, onViewHistory, onDelete])

  return (
    <DataGrid
      columns={columns}
      data={schedules}
      isLoading={loading}
      loadingMessage="Loading your schedules…"
      storageKey="report-schedules"
      rowNoun="schedule"
      /* Keeps the name the card had, so every existing assertion about a
         schedule's rendered content keeps describing the shipped DOM rather than
         being rewritten to chase a structural change it does not care about. */
      rowTestId="schedule-card"
      exportFilename="report-schedules"
      stickyHeader
      normalCaseHeaders
      // One page of standing instructions is not a data set to filter — and the
      // list route caps at 100 with no cursor, so a filter row would imply a
      // completeness this page cannot offer. The cap is disclosed in the card
      // header instead (see ReportSchedules).
      enableFiltering={false}
      pageSize={25}
      keepPageOnDataChange
      defaultSorting={[{ id: 'nextRunAt', desc: false }]}
    />
  )
}

export default SchedulesTable
